import { useState } from 'react';

import { Button, Link, Text } from '@rbilabs/universal-components';
import { Image, ImageBackground } from 'expo-image';
import { useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';

import { CircularProgress } from 'components/circular-progress';
import { QuestDetailModal } from 'components/quests/quest-detail-modal';
import { QuestTerms } from 'components/quests/quest-drawer-content/quest-terms';
import { IQuestTermsProps } from 'components/quests/quest-drawer-content/types';
import { LoyaltyQuestStatus } from 'generated/graphql-gateway';
import { routes } from 'navigation/routing';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants/primitives';

import { RedeemButton } from '../profile-card/components/styled';
import { styles as sharedStyles } from '../styles';

const crown = require('assets/images/nav/rewards-active.svg');

const megaphone = require('./user-crown.png');

export const ChallengeProgressCard = ({
  crownsAmount,
  challengeStatus,
  completedSteps,
  totalSteps,
  terms,
}: {
  crownsAmount: number;
  challengeStatus: LoyaltyQuestStatus;
  completedSteps: number;
  totalSteps: number;
  terms?: IQuestTermsProps['cmsQuestTerms'];
}) => {
  const [showTerms, setShowTerms] = useState(false);
  const { navigate } = useRouter();

  const TITLE_BY_STATUS = {
    [LoyaltyQuestStatus.NOT_ENROLLED]: t('finishYourProfile', 'Finish Your Profile Completion'),
    [LoyaltyQuestStatus.ACTIVATED]: t('finishYourProfile', 'Finish Your Profile Completion'),
    [LoyaltyQuestStatus.NOT_COMPLETED]: t('keepGoing', "Keep Going — You're Close!"),
    [LoyaltyQuestStatus.COMPLETED]: `${t('profileCompleted', 'Profile Completed')} 🎉`,
  };

  const DESCRIPTION_BY_STATUS = {
    [LoyaltyQuestStatus.NOT_ENROLLED]: t('earnCrownsToUnlockRewards', {
      crowns: crownsAmount,
      defaultValue: 'Earn {crowns} Crowns and unlock exclusive rewards',
    }),
    [LoyaltyQuestStatus.ACTIVATED]: t('earnCrownsToUnlockRewards', {
      crowns: crownsAmount,
      defaultValue: 'Earn {crowns} Crowns and unlock exclusive rewards',
    }),
    [LoyaltyQuestStatus.NOT_COMPLETED]: t('finishProfileToEarnCrowns', {
      crowns: crownsAmount,
      defaultValue: 'Finish your profile to earn {crowns} Crowns',
    }),
    [LoyaltyQuestStatus.COMPLETED]: t('youEarnedCrowns', {
      crowns: crownsAmount,
      defaultValue: 'You earned {crowns} Crowns! Tap below to claim your reward.',
    }),
  };

  const isChallengeCompleted = challengeStatus === LoyaltyQuestStatus.COMPLETED;
  const showStepProgress = completedSteps > 0;

  const leftTag = (
    <View style={[styles.baseTag, isChallengeCompleted && styles.earnedCrownsTag]}>
      <Image
        source={crown}
        style={styles.tagIcon}
        tintColor={isChallengeCompleted ? primitive.bk.$bbqBrown : primitive.bk.$meltyYellow}
      />
      <Text fontSize="2xs">
        {isChallengeCompleted ? `Earned ${crownsAmount}` : `Earn ${crownsAmount}`}
      </Text>
    </View>
  );

  return (
    <>
      <View style={[styles.card, sharedStyles.cardElevationStyles]}>
        {showStepProgress ? (
          <View style={styles.left}>
            {leftTag}
            <CircularProgress
              size={90}
              bgColor="rgba(255, 255, 255, 0.3)"
              pgColor="white"
              strokeWidth={8}
              progressPercent={(completedSteps / totalSteps) * 100}
              contentStyle={styles.circularProgressContent}
            >
              <Text color="white" fontFamily="heading" fontSize="3xl" lineHeight="3xl">
                {completedSteps}{' '}
              </Text>
              <Text color="white" fontFamily="heading" fontSize="lg" lineHeight="lg">
                / {totalSteps}
              </Text>
            </CircularProgress>
          </View>
        ) : (
          <ImageBackground style={[styles.left, styles.leftNoProgress]} source={megaphone}>
            {leftTag}
          </ImageBackground>
        )}
        <View style={styles.right}>
          <Text fontFamily="heading" fontSize="sm">
            {TITLE_BY_STATUS[challengeStatus]}
          </Text>
          <Text fontSize="xs">{DESCRIPTION_BY_STATUS[challengeStatus]}</Text>
          <View style={styles.footer}>
            {challengeStatus === LoyaltyQuestStatus.COMPLETED ? (
              <Button
                variant="outline"
                size="sm"
                width="100%"
                accessibilityRole="link"
                onPress={() => navigate(routes.rewards)}
              >
                {t('claimReward', 'Claim Reward')}
              </Button>
            ) : (
              <>
                <RedeemButton
                  style={styles.footerHalfWidthButton}
                  accessibilityRole="link"
                  onPress={() => navigate('/challenges/user-journey')}
                >
                  {t('complete', 'Complete')}
                </RedeemButton>
                {terms && (
                  <Link
                    _text={{ fontSize: 'xs' }}
                    style={styles.footerHalfWidthButton}
                    onPress={() => setShowTerms(true)}
                  >
                    {t('viewTerms', 'View Terms')}
                  </Link>
                )}
              </>
            )}
          </View>
        </View>
      </View>
      {terms && showTerms && (
        <QuestDetailModal hideTitle onDismiss={() => setShowTerms(false)} title="">
          <QuestTerms cmsQuestTerms={terms} />
        </QuestDetailModal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    backgroundColor: primitive.$white,
    borderRadius: 12,
    overflow: 'hidden',
  },
  left: {
    backgroundColor: primitive.bk.$bbqBrown,
    paddingHorizontal: primitive.spacing.$5,
    paddingVertical: primitive.spacing.$6,
    position: 'relative',
    justifyContent: 'center',
    flex: 1,
    maxWidth: 145,
    minWidth: 125,
  },
  leftNoProgress: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  right: {
    paddingHorizontal: primitive.spacing.$4,
    paddingVertical: primitive.spacing.$6,
    gap: primitive.spacing.$2,
    flex: 1,
    minWidth: 200,
  },
  footer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: primitive.spacing.$4,
  },
  footerHalfWidthButton: {
    flex: 1,
  },
  baseTag: {
    borderRadius: 4,
    backgroundColor: primitive.$white,
    flexDirection: 'row',
    gap: primitive.spacing.$1,
    alignItems: 'center',
    paddingHorizontal: 6,
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 1,
  },
  earnedCrownsTag: {
    backgroundColor: primitive.bk.$meltyYellow,
  },
  tagIcon: {
    width: 12,
    height: 12,
  },
  circularProgressContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
